import * as React from 'react';

import { observer } from 'mobx-react';

import { Button } from '@estee/elc-buttons';

import { Alert, AlertActionContainer } from '~components/Alert';
import { PasswordHelp } from '~components/PasswordHelp';
import { goBindAccountWithLine, MessageType } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { MessageGroup } from '~repositories/MessageRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { classes } from '~utils/Classes';
import { IamForms, useConfig } from '~utils/hooks/config';
import { useTranslation } from '~utils/hooks/translations';
import { isLinkValid } from '~utils/TranslationLabelUtils';
import { DynamicForm } from '~views/common/DynamicForm';
import { MessagePanel } from '~views/common/MessagePanel';
import { SocialButton } from '~views/social-sign-in/SocialButton';

import { SocialPositionWrapper } from '../social-sign-in/SocialPositionWrapper';
import { AccountSignInFormViewController } from './AccountSignInFormViewController';

export interface IAccountSignInFormProps {
    submitButtonLabel?: string;
}

const wrapperClassName = classes('iam-account-sign-in-form');
const signInButtonClassName = classes('iam-sign-in-button');

const otpSignInEnabled = false; // TODO: this should come from config

export const AccountSignInForm = observer((props: IAccountSignInFormProps) => {
    const {
        data: { formStore, infoMessages, errorMessages, messages, socialConfig, hideSocialButtons },
        signInButtonClicked,
        resetPasswordClicked,
        emailMeCodeToSignInClicked
    }: AccountSignInFormViewController = diContainer.get(
        serviceNames.accountSignInFormViewController
    );

    const { isSubmitting } = formStore;

    const {
        fieldsValidations: validations = {},
        schema = {
            sections: []
        }
    } = useConfig(IamForms.signIn);

    const translations = useTranslation();
    const { signInLabel, loadingLabel, [goBindAccountWithLine]: bindWithLine } = translations;
    const inStoreSignInMessage = messages.get(MessageGroup.IN_STORE_SIGN_IN_TO_CONTINUE)?.[0];
    const { message, transKey } = inStoreSignInMessage || {};

    return (
        <div className={wrapperClassName}>
            {inStoreSignInMessage && (
                <Alert messageType={MessageType.INFO}>
                    <p>{message || (transKey && translations[transKey])}</p>
                    {isLinkValid(bindWithLine) && (
                        <AlertActionContainer className="elc-iam-alert-sign-in-to-continue">
                            <SocialButton
                                provider={SignInType.LINE}
                                onClick={() => window.open(bindWithLine.path, '_blank')}
                                buttonStyle="rect"
                            >
                                {bindWithLine.title}
                            </SocialButton>
                        </AlertActionContainer>
                    )}
                </Alert>
            )}
            <MessagePanel messages={infoMessages} />
            <MessagePanel messages={errorMessages} />
            <SocialPositionWrapper
                socialWrapperConfig={socialConfig}
                hideSocialButtons={hideSocialButtons}
            >
                <>
                    <DynamicForm
                        name="signInForm"
                        formSchema={schema}
                        validations={validations}
                        formStore={formStore}
                        submit={signInButtonClicked}
                    />
                    <Button
                        className={signInButtonClassName}
                        disabled={isSubmitting}
                        onClick={signInButtonClicked}
                    >
                        {isSubmitting ? loadingLabel : props.submitButtonLabel || signInLabel}
                    </Button>
                    {otpSignInEnabled && (
                        <PasswordHelp
                            isSubmitting={isSubmitting}
                            resetPasswordClicked={resetPasswordClicked}
                            emailMeCodeToSignInClicked={emailMeCodeToSignInClicked}
                        />
                    )}
                </>
            </SocialPositionWrapper>
        </div>
    );
});
